import React, { useState, useEffect } from 'react';
import { 
  Code, 
  Edit, 
  Trash2, 
  Play,
  Plus,
  Check,
  X,
  Globe,
  Key,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  Copy
} from 'react-feather';
import { useAuth } from '../../contexts/AuthContext';
import '../../../public/styles/eddy-shared.css';

/**
 * Widget Integration data structure matching backend configuration
 */
interface WidgetIntegration {
  id: string;
  name: string;
  apiKey: string;
  domains: string[];
  widget: {
    title: string;
    subtitle: string;
    primaryColor: string;
    accentColor: string;
    logo?: string | null;
    position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
    autoOpen: boolean;
    showSources: boolean;
    placeholder: string;
    quickQuestions: string[];
  };
  features: {
    maxMessagesPerSession: number;
    maxSessionsPerDay: number;
    allowedUserTypes: string[];
  };
  status: 'active' | 'inactive';
}

interface WidgetIntegrationsResponse {
  success: boolean;
  data: WidgetIntegration[];
  total: number;
  error?: string;
}

interface TestResult {
  success: boolean;
  domain: string;
  allowed: boolean;
  integration?: any;
  error?: string;
  validation?: {
    domainValid: boolean;
    apiKeyValid: boolean;
    configurationValid: boolean;
  };
  errors?: string[];
}

const WidgetIntegrations: React.FC = () => {
  const { user } = useAuth();
  const [integrations, setIntegrations] = useState<WidgetIntegration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<WidgetIntegration | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState<Record<string, boolean>>({});
  const [testDomain, setTestDomain] = useState('');
  const [testResults, setTestResults] = useState<TestResult | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Check if user can manage widget integrations (Admin+ only)
  const canManageWidgets = user?.capabilities?.canUseTeaching || false;

  // Load widget integrations
  const loadIntegrations = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/widget-integrations');
      const data: WidgetIntegrationsResponse = await response.json();
      
      if (data.success && Array.isArray(data.data)) {
        setIntegrations(data.data);
      } else {
        setError(data.error || 'Failed to load widget integrations');
      }
    } catch (err) {
      setError('Failed to load widget integrations');
      console.error('Error loading widget integrations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIntegrations();
  }, []);

  // Show notification
  const showNotification = (message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Create new integration
  const handleCreate = async (formData: any) => {
    try {
      setModalLoading(true);
      setModalError(null);
      
      const response = await fetch('/api/admin/widget-integrations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        showNotification('Widget integration created successfully', 'success');
        setShowCreateModal(false);
        loadIntegrations();
      } else {
        setModalError(data.error || 'Failed to create widget integration');
      }
    } catch (err) {
      setModalError('Failed to create widget integration');
    } finally {
      setModalLoading(false);
    }
  };

  // Update integration
  const handleUpdate = async (id: string, formData: any) => {
    try {
      setModalLoading(true);
      setModalError(null);
      
      const response = await fetch(`/api/admin/widget-integrations/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        showNotification('Widget integration updated successfully', 'success');
        setEditingIntegration(null);
        loadIntegrations();
      } else {
        setModalError(data.error || 'Failed to update widget integration');
      }
    } catch (err) {
      setModalError('Failed to update widget integration');
    } finally {
      setModalLoading(false);
    }
  };

  // Delete integration
  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete the widget integration "${name}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await fetch(`/api/admin/widget-integrations/${id}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        showNotification('Widget integration deleted successfully', 'success');
        loadIntegrations();
      } else {
        showNotification(data.error || 'Failed to delete widget integration', 'error');
      }
    } catch (err) {
      showNotification('Failed to delete widget integration', 'error');
    }
  };

  // Test integration
  const handleTest = async (id: string) => {
    if (!testDomain) {
      showNotification('Please enter a domain to test', 'error');
      return;
    }
    
    try {
      setTestingId(id);
      setTestResults(null);
      
      const response = await fetch(`/api/admin/widget-integrations/${id}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: testDomain })
      });
      
      const data = await response.json();
      setTestResults(data);
    } catch (err) {
      setTestResults({
        success: false,
        domain: testDomain,
        allowed: false,
        error: 'Failed to test integration'
      });
    } finally {
      setTestingId(null);
    }
  };

  // Copy embed code
  const copyEmbedCode = (apiKey: string) => {
    const safeApiKey = apiKey.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const embedCode = `<link rel="stylesheet" href="${window.location.origin}/widget/eddy-widget.css">
<script src="${window.location.origin}/widget/eddy-widget.js"
    data-api-key="${safeApiKey}"
    data-api-endpoint="/api/widget/chat"
    data-position="bottom-right">
</script>`;

    navigator.clipboard.writeText(embedCode);
    showNotification('Embed code copied to clipboard', 'success');
  };

  // Render loading state
  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-flex items-center space-x-2 text-eddy-primary">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-eddy-blue"></div>
          <span>Loading widget integrations...</span>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-2xl mx-auto">
        <div className="flex items-center space-x-2 text-red-700">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Code className="text-eddy-blue" size={28} />
          <div>
            <h1 className="text-2xl font-semibold text-eddy-primary">Widget Integrations</h1>
            <p className="text-gray-600 text-sm">
              {canManageWidgets 
                ? 'Manage chat widget configurations and deployments'
                : 'View widget configurations (Admin role required for management)'
              }
            </p>
          </div>
        </div>
        {canManageWidgets && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="eddy-button flex items-center space-x-2"
          >
            <Plus size={16} />
            <span>Add Integration</span>
          </button>
        )}
      </div>

      {/* Notification */}
      {notification && (
        <div className={`mb-4 p-4 rounded-lg flex items-center space-x-2 ${
          notification.type === 'success' 
            ? 'bg-eddy-green/10 text-eddy-green border border-eddy-green/20' 
            : 'bg-eddy-orange/10 text-eddy-orange border border-eddy-orange/20'
        }`}>
          {notification.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          <span>{notification.message}</span>
        </div>
      )}

      {/* Integrations List */}
      <div className="eddy-card">
        {integrations.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Code size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-lg mb-2">No widget integrations found</p>
            <p className="text-sm">Create your first integration to start embedding Eddy on external websites.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-eddy-light-gray">
                  <th className="text-left py-3 px-4 font-medium text-eddy-primary">Name</th>
                  <th className="text-left py-3 px-4 font-medium text-eddy-primary">API Key</th>
                  <th className="text-left py-3 px-4 font-medium text-eddy-primary">Domains</th>
                  <th className="text-left py-3 px-4 font-medium text-eddy-primary">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-eddy-primary">Configuration</th>
                  <th className="text-right py-3 px-4 font-medium text-eddy-primary">Actions</th>
                </tr>
              </thead>
              <tbody>
                {integrations.map((integration) => (
                  <tr key={integration.id} className="border-b border-eddy-light-gray hover:bg-eddy-light-gray/50">
                    <td className="py-4 px-4">
                      <div className="font-medium text-eddy-primary">{integration.name}</div>
                      <div className="text-sm text-gray-500">ID: {integration.id}</div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        <code className="bg-eddy-light-gray px-2 py-1 rounded text-sm font-mono">
                          {showApiKey[integration.id] ? integration.apiKey : '••••••••••••••••'}
                        </code>
                        <button
                          onClick={() => setShowApiKey(prev => ({ ...prev, [integration.id]: !prev[integration.id] }))}
                          className="text-eddy-blue hover:text-eddy-blue/80"
                        >
                          {showApiKey[integration.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                        <button
                          onClick={() => copyEmbedCode(integration.apiKey)}
                          className="text-eddy-blue hover:text-eddy-blue/80"
                          title="Copy embed code"
                        >
                          <Copy size={16} />
                        </button>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="max-w-xs">
                        {integration.domains.slice(0, 2).map((domain, idx) => (
                          <div key={idx} className="flex items-center space-x-1 text-sm">
                            <Globe size={14} className="text-gray-400" />
                            <span>{domain}</span>
                          </div>
                        ))}
                        {integration.domains.length > 2 && (
                          <div className="text-sm text-gray-500">
                            +{integration.domains.length - 2} more
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                        integration.status === 'active' 
                          ? 'bg-eddy-green/10 text-eddy-green' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {integration.status === 'active' ? <CheckCircle size={12} /> : <X size={12} />}
                        <span>{integration.status}</span>
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="text-sm space-y-1">
                        <div><strong>Title:</strong> {integration.widget.title}</div>
                        <div><strong>Position:</strong> {integration.widget.position}</div>
                        <div className="flex items-center space-x-1">
                          <strong>Color:</strong>
                          <span 
                            className="inline-block w-4 h-4 rounded border border-gray-300" 
                            style={{ backgroundColor: integration.widget.primaryColor }}
                          ></span>
                          <span className="font-mono text-xs">{integration.widget.primaryColor}</span>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end space-x-2">
                        {canManageWidgets ? (
                          <>
                            <button
                              onClick={() => setEditingIntegration(integration)}
                              className="eddy-button eddy-button-success"
                            >
                              <Edit size={16} />
                              <span>Edit</span>
                            </button>
                            <button
                              onClick={() => handleDelete(integration.id, integration.name)}
                              className="eddy-button eddy-button-danger"
                            >
                              <Trash2 size={16} />
                              <span>Delete</span>
                            </button>
                          </>
                        ) : (
                          <div className="text-gray-400 text-xs">
                            Admin access required
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Test Integration Section */}
      {integrations.length > 0 && (
        <div className="eddy-card mt-6">
          <h3 className="text-lg font-semibold text-eddy-primary mb-4 flex items-center space-x-2">
            <Play className="text-eddy-blue" size={20} />
            <span>Test Integration</span>
          </h3>
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <select 
                className="eddy-input flex-1"
                value={testingId || ''}
                onChange={(e) => setTestingId(e.target.value)}
              >
                <option value="">Select an integration to test</option>
                {integrations.map(integration => (
                  <option key={integration.id} value={integration.id}>
                    {integration.name} ({integration.apiKey})
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Enter domain to test (e.g., example.com)"
                className="eddy-input flex-1"
                value={testDomain}
                onChange={(e) => setTestDomain(e.target.value)}
              />
              <button
                onClick={() => testingId && handleTest(testingId)}
                disabled={!testingId || !testDomain}
                className="eddy-button"
              >
                <Play size={16} />
                <span>Test</span>
              </button>
            </div>

            {testResults && (
              <div className={`p-4 rounded-lg border ${
                testResults.success && testResults.allowed
                  ? 'bg-eddy-green/10 border-eddy-green/30'
                  : 'bg-eddy-orange/10 border-eddy-orange/30'
              }`}>
                <div className="flex items-center space-x-2 mb-2">
                  {testResults.success && testResults.allowed ? (
                    <CheckCircle className="text-eddy-green" size={20} />
                  ) : (
                    <AlertCircle className="text-eddy-orange" size={20} />
                  )}
                  <span className="font-medium">
                    {testResults.allowed ? 'Domain is allowed' : 'Domain is not allowed'}
                  </span>
                </div>
                {testResults.validation && (
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center space-x-2">
                      {testResults.validation.domainValid ? (
                        <Check className="text-eddy-green" size={16} />
                      ) : (
                        <X className="text-eddy-orange" size={16} />
                      )}
                      <span>Domain validation</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {testResults.validation.apiKeyValid ? (
                        <Check className="text-eddy-green" size={16} />
                      ) : (
                        <X className="text-eddy-orange" size={16} />
                      )}
                      <span>API key validation</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {testResults.validation.configurationValid ? (
                        <Check className="text-eddy-green" size={16} />
                      ) : (
                        <X className="text-eddy-orange" size={16} />
                      )}
                      <span>Configuration validation</span>
                    </div>
                  </div>
                )}
                {testResults.errors && testResults.errors.length > 0 && (
                  <div className="mt-2 text-sm text-eddy-orange">
                    {testResults.errors.map((error, idx) => (
                      <div key={idx}>• {error}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Modal (Admin+ only) */}
      {canManageWidgets && (showCreateModal || editingIntegration) && (
        <IntegrationModal
          integration={editingIntegration}
          onSave={(data) => {
            if (editingIntegration) {
              handleUpdate(editingIntegration.id, data);
            } else {
              handleCreate(data);
            }
          }}
          onClose={() => {
            setShowCreateModal(false);
            setEditingIntegration(null);
            setModalError(null);
          }}
          loading={modalLoading}
          error={modalError}
        />
      )}
    </div>
  );
};

/**
 * Modal component for creating/editing widget integrations
 */
interface IntegrationModalProps {
  integration: WidgetIntegration | null;
  onSave: (data: any) => void;
  onClose: () => void;
  loading: boolean;
  error: string | null;
}

const IntegrationModal: React.FC<IntegrationModalProps> = ({ 
  integration, 
  onSave, 
  onClose, 
  loading, 
  error 
}) => {
  const [formData, setFormData] = useState<Partial<WidgetIntegration>>({
    id: '',
    name: '',
    apiKey: '',
    domains: [],
    widget: {
      title: 'Ask Eddy',
      subtitle: 'Edelweiss AI Assistant',
      primaryColor: '#5b86a9',
      accentColor: '#849157',
      logo: null,
      position: 'bottom-right',
      autoOpen: false,
      showSources: true,
      placeholder: 'Ask me anything about Edelweiss...',
      quickQuestions: [
        'What is Edelweiss?',
        'How do I create an order?',
        'How do I request review copies?',
        'What are event grids?'
      ]
    },
    features: {
      maxMessagesPerSession: 50,
      maxSessionsPerDay: 100,
      allowedUserTypes: ['all']
    },
    status: 'active'
  });

  const [domainInput, setDomainInput] = useState('');
  const [quickQuestionInput, setQuickQuestionInput] = useState('');
  const [userTypeInput, setUserTypeInput] = useState('');

  useEffect(() => {
    if (integration) {
      setFormData(integration);
    }
  }, [integration]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const addDomain = () => {
    if (domainInput && formData.domains && !formData.domains.includes(domainInput)) {
      setFormData(prev => ({
        ...prev,
        domains: [...(prev.domains || []), domainInput]
      }));
      setDomainInput('');
    }
  };

  const removeDomain = (domain: string) => {
    setFormData(prev => ({
      ...prev,
      domains: prev.domains?.filter(d => d !== domain) || []
    }));
  };

  const addQuickQuestion = () => {
    if (quickQuestionInput && formData.widget && !formData.widget.quickQuestions.includes(quickQuestionInput)) {
      setFormData(prev => ({
        ...prev,
        widget: {
          ...prev.widget!,
          quickQuestions: [...prev.widget!.quickQuestions, quickQuestionInput]
        }
      }));
      setQuickQuestionInput('');
    }
  };

  const removeQuickQuestion = (question: string) => {
    setFormData(prev => ({
      ...prev,
      widget: {
        ...prev.widget!,
        quickQuestions: prev.widget!.quickQuestions.filter(q => q !== question)
      }
    }));
  };

  const addUserType = () => {
    if (userTypeInput && formData.features && !formData.features.allowedUserTypes.includes(userTypeInput)) {
      setFormData(prev => ({
        ...prev,
        features: {
          ...prev.features!,
          allowedUserTypes: [...prev.features!.allowedUserTypes, userTypeInput]
        }
      }));
      setUserTypeInput('');
    }
  };

  const removeUserType = (userType: string) => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features!,
        allowedUserTypes: prev.features!.allowedUserTypes.filter(t => t !== userType)
      }
    }));
  };

  const generateApiKey = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let key = '';
    for (let i = 0; i < 32; i++) {
      key += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, apiKey: key }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-eddy-light-gray px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-eddy-primary">
              {integration ? 'Edit Widget Integration' : 'Create Widget Integration'}
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-eddy-orange/10 border border-eddy-orange/30 rounded-lg p-4 flex items-center space-x-2 text-eddy-orange">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-eddy-primary">Basic Information</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Integration ID
                </label>
                <input
                  type="text"
                  className="eddy-input"
                  value={formData.id}
                  onChange={(e) => setFormData(prev => ({ ...prev, id: e.target.value }))}
                  placeholder="unique-id"
                  required
                  disabled={!!integration}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  className="eddy-input"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="My Widget Integration"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API Key
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  className="eddy-input flex-1 font-mono text-sm"
                  value={formData.apiKey}
                  onChange={(e) => setFormData(prev => ({ ...prev, apiKey: e.target.value }))}
                  placeholder="your-api-key-here"
                  required
                />
                <button
                  type="button"
                  onClick={generateApiKey}
                  className="eddy-button"
                >
                  <Key size={16} />
                  <span>Generate</span>
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                className="eddy-input"
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as 'active' | 'inactive' }))}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Allowed Domains */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-eddy-primary">Allowed Domains</h3>
            
            <div className="flex items-center space-x-2">
              <input
                type="text"
                className="eddy-input flex-1"
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                placeholder="example.com or *.example.com"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDomain())}
              />
              <button
                type="button"
                onClick={addDomain}
                className="eddy-button"
              >
                <Plus size={16} />
                <span>Add</span>
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {formData.domains?.map((domain, idx) => (
                <div key={idx} className="flex items-center space-x-1 bg-eddy-light-gray px-3 py-1 rounded-full">
                  <Globe size={14} className="text-gray-500" />
                  <span className="text-sm">{domain}</span>
                  <button
                    type="button"
                    onClick={() => removeDomain(domain)}
                    className="text-gray-500 hover:text-eddy-orange"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Widget Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-eddy-primary">Widget Configuration</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  className="eddy-input"
                  value={formData.widget?.title}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    widget: { ...prev.widget!, title: e.target.value }
                  }))}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subtitle
                </label>
                <input
                  type="text"
                  className="eddy-input"
                  value={formData.widget?.subtitle}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    widget: { ...prev.widget!, subtitle: e.target.value }
                  }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Primary Color
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    className="h-10 w-20"
                    value={formData.widget?.primaryColor}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      widget: { ...prev.widget!, primaryColor: e.target.value }
                    }))}
                  />
                  <input
                    type="text"
                    className="eddy-input flex-1"
                    value={formData.widget?.primaryColor}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      widget: { ...prev.widget!, primaryColor: e.target.value }
                    }))}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Accent Color
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    className="h-10 w-20"
                    value={formData.widget?.accentColor}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      widget: { ...prev.widget!, accentColor: e.target.value }
                    }))}
                  />
                  <input
                    type="text"
                    className="eddy-input flex-1"
                    value={formData.widget?.accentColor}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      widget: { ...prev.widget!, accentColor: e.target.value }
                    }))}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Position
                </label>
                <select
                  className="eddy-input"
                  value={formData.widget?.position}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    widget: { ...prev.widget!, position: e.target.value as any }
                  }))}
                >
                  <option value="bottom-right">Bottom Right</option>
                  <option value="bottom-left">Bottom Left</option>
                  <option value="top-right">Top Right</option>
                  <option value="top-left">Top Left</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Logo URL (optional)
                </label>
                <input
                  type="text"
                  className="eddy-input"
                  value={formData.widget?.logo || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    widget: { ...prev.widget!, logo: e.target.value || null }
                  }))}
                  placeholder="https://example.com/logo.png"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Placeholder Text
              </label>
              <input
                type="text"
                className="eddy-input"
                value={formData.widget?.placeholder}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  widget: { ...prev.widget!, placeholder: e.target.value }
                }))}
              />
            </div>

            <div className="space-x-4">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  className="form-checkbox text-eddy-blue"
                  checked={formData.widget?.autoOpen}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    widget: { ...prev.widget!, autoOpen: e.target.checked }
                  }))}
                />
                <span className="ml-2">Auto-open on page load</span>
              </label>
              
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  className="form-checkbox text-eddy-blue"
                  checked={formData.widget?.showSources}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    widget: { ...prev.widget!, showSources: e.target.checked }
                  }))}
                />
                <span className="ml-2">Show source references</span>
              </label>
            </div>
          </div>

          {/* Quick Questions */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-eddy-primary">Quick Questions</h3>
            
            <div className="flex items-center space-x-2">
              <input
                type="text"
                className="eddy-input flex-1"
                value={quickQuestionInput}
                onChange={(e) => setQuickQuestionInput(e.target.value)}
                placeholder="Add a quick question"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addQuickQuestion())}
              />
              <button
                type="button"
                onClick={addQuickQuestion}
                className="eddy-button"
              >
                <Plus size={16} />
                <span>Add</span>
              </button>
            </div>

            <div className="space-y-2">
              {formData.widget?.quickQuestions.map((question, idx) => (
                <div key={idx} className="flex items-center justify-between bg-eddy-light-gray p-2 rounded">
                  <span className="text-sm">{question}</span>
                  <button
                    type="button"
                    onClick={() => removeQuickQuestion(question)}
                    className="text-gray-500 hover:text-eddy-orange"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Features */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-eddy-primary">Features & Limits</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Messages per Session
                </label>
                <input
                  type="number"
                  className="eddy-input"
                  value={formData.features?.maxMessagesPerSession}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    features: { ...prev.features!, maxMessagesPerSession: parseInt(e.target.value) }
                  }))}
                  min="1"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Sessions per Day
                </label>
                <input
                  type="number"
                  className="eddy-input"
                  value={formData.features?.maxSessionsPerDay}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    features: { ...prev.features!, maxSessionsPerDay: parseInt(e.target.value) }
                  }))}
                  min="1"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Allowed User Types
              </label>
              <div className="flex items-center space-x-2 mb-2">
                <input
                  type="text"
                  className="eddy-input flex-1"
                  value={userTypeInput}
                  onChange={(e) => setUserTypeInput(e.target.value)}
                  placeholder="e.g., bookseller, librarian, publisher, or 'all'"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addUserType())}
                />
                <button
                  type="button"
                  onClick={addUserType}
                  className="eddy-button"
                >
                  <Plus size={16} />
                  <span>Add</span>
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.features?.allowedUserTypes.map((type, idx) => (
                  <div key={idx} className="flex items-center space-x-1 bg-eddy-light-gray px-3 py-1 rounded-full">
                    <span className="text-sm">{type}</span>
                    <button
                      type="button"
                      onClick={() => removeUserType(type)}
                      className="text-gray-500 hover:text-eddy-orange"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-eddy-light-gray">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="eddy-button eddy-button-primary"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Check size={16} />
                  <span>{integration ? 'Update' : 'Create'} Integration</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WidgetIntegrations;